import { ButtonGroup, SkeletonLoader } from '@toggle/design-system';
import { useElementSize } from '@toggle/helpers';
import { Entity } from '@toggle/toggle';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { usePriceMarketReactionData } from '~/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { MarketReactionChart } from '~/modules/market-reaction-charts/MarketReactionChart';
import { useGetEarningById } from '~/react-query-hooks/earnings/useGetEarningById';
import { useScenarios } from '~/react-query-hooks/scenarios/useScenarios';
import { Expectation } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/types';

import { HistoricalReaction } from './historical-reaction/HistoricalReaction';
import styles from './MarketReactionSection.module.scss';

interface MarketReactionSectionProps {
  entity: Entity;
  earningsId: string;
  reportingDate: string;
}

const START_HORIZON = '-2d';
const END_HORIZON = '5d';

export const MarketReactionSection = ({
  entity,
  earningsId,
  reportingDate,
}: MarketReactionSectionProps) => {
  const { t } = useTranslation(['earnings']);
  const chartWrapperRef = useRef(null);
  const { width } = useElementSize(chartWrapperRef);
  const buttonOptions = useMemo(
    () => [
      t('earnings:earningsInsight.drawer.earningsRecap.marketReaction'),
      t('earnings:earningsInsight.drawer.earningsRecap.historicalReaction'),
    ],
    [t]
  );
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);
  const isMarketReactionSelected = selectedButtonIndex === 0;
  const lastHistoricalRequestKeyRef = useRef<string | null>(null);
  const {
    sendMessage: sendHistoricalScenarios,
    data: historicalScenariosData,
    isLoading: isHistoricalScenariosLoading,
    error: hasHistoricalScenariosError,
  } = useScenarios();

  const {
    data: earningsData,
    isLoading: isEarningsLoading,
    isError: isEarningsError,
    error: earningsError,
  } = useGetEarningById(earningsId);

  const {
    priceData,
    fromDateEST,
    toDateEST,
    lastProportionChange,
    lastKnownPrice,
    prevCloseData,
    lastKnownDate,
    isLoading: isMarketReactionDataLoading,
  } = usePriceMarketReactionData({
    ticker: entity?.subscribable_ticker ?? '',
    entityTag: entity.tag,
    reportingDate,
  });

  const hasEpsData =
    !isEarningsLoading && earningsData?.eps?.surprise !== undefined;
  const hasError = isEarningsError || hasHistoricalScenariosError;
  const errorMessage = earningsError?.message;
  const epsSurprise = earningsData?.eps?.surprise;
  const expectation =
    epsSurprise !== undefined
      ? epsSurprise >= 0
        ? Expectation.Beat
        : Expectation.Miss
      : undefined;

  useEffect(() => {
    if (
      isMarketReactionSelected ||
      !earningsData ||
      !expectation ||
      isEarningsLoading
    ) {
      return;
    }

    const condition = expectation === Expectation.Beat ? 'above' : 'below';
    const expectedValue = expectation === Expectation.Beat ? 0.01 : -0.01;

    const payload = {
      entities: [{ tag: entity.tag }],
      conditions: [
        {
          condition: condition,
          snake: `${entity.tag}.surprise_absolute_eps`,
          value: epsSurprise ?? expectedValue,
        },
      ],
      start_horizon: START_HORIZON,
      end_horizon: END_HORIZON,
      skip_report: true,
      non_overlapping: true,
    };

    const historicalRequestKey = JSON.stringify({
      earningsId,
      entityTag: entity.tag,
      expectation,
      epsSurprise: epsSurprise ?? expectedValue,
    });

    if (lastHistoricalRequestKeyRef.current === historicalRequestKey) {
      return;
    }

    lastHistoricalRequestKeyRef.current = historicalRequestKey;
    sendHistoricalScenarios(payload);
  }, [
    earningsData,
    earningsId,
    entity.tag,
    epsSurprise,
    expectation,
    isEarningsLoading,
    isMarketReactionSelected,
    sendHistoricalScenarios,
  ]);

  const renderContent = () => {
    const shouldRenderMarketReactionChart =
      !hasEpsData || isMarketReactionSelected;
    const shouldShowLoader = shouldRenderMarketReactionChart
      ? isEarningsLoading || isMarketReactionDataLoading
      : isEarningsLoading || isHistoricalScenariosLoading;

    if (hasError) {
      return (
        <div data-testid="error-message">
          {errorMessage ?? t('earnings:empty.description')}
        </div>
      );
    }

    if (shouldShowLoader) {
      return (
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '300px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      );
    }

    if (shouldRenderMarketReactionChart) {
      if (!toDateEST || priceData.length === 0) {
        return (
          <h2 className={styles.title}>{t('earnings:empty.description')}</h2>
        );
      }

      return (
        <ErrorBoundaryWrapper>
          <MarketReactionChart
            fromDateEST={fromDateEST}
            toDateEST={toDateEST}
            lines={[
              {
                data: priceData,
                prevCloseData,
                lastProportionChange,
                lastKnownPrice,
                lastKnownDate,
              },
            ]}
            entities={[entity]}
          />
        </ErrorBoundaryWrapper>
      );
    }

    if (!historicalScenariosData.length || !earningsData) {
      return (
        <h2 className={styles.title}>{t('earnings:empty.description')}</h2>
      );
    }

    return (
      <HistoricalReaction
        entity={entity}
        earningsData={earningsData}
        scenariosData={historicalScenariosData}
        width={width}
      />
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        {hasEpsData && (
          <div className={styles.styledButtonGroup}>
            <ButtonGroup
              labels={buttonOptions}
              activeIndex={selectedButtonIndex}
              onClick={setSelectedButtonIndex}
            />
          </div>
        )}
      </div>
      <div className={styles.contentWrapper} ref={chartWrapperRef}>
        {renderContent()}
      </div>
    </div>
  );
};
